import React, { useMemo, useState, useRef, useCallback, useEffect, useContext } from 'react';
import { Group, GROUP_COLORS, Pouch } from '../types';
import { getPathBounds, calculateGroupSubtotal, isGroupMergeDisabled, shouldShowTotalLabel, getContainedGroups, generateRoundedRectPath } from '../logic/GroupLogic';
import { getPouchEffectiveContribution } from '../logic/BalancingPouchLogic';
import { NumberCounter } from './NumberCounterComponent';
import { GroupZIndexManager } from '../utils/GroupZIndexManager';
import { getModifierKeys } from '../App';
import {
  formatTooltip,
  formatGroupGuideEditPcExtrasHtml,
  formatGroupGuideDeletePcExtrasHtml,
} from '../utils/tooltipFormatter';
import { resolveGroupColorHotkey, GROUP_COLOR_HOTKEY_DISPLAY_ORDER } from '../utils/groupColorHotkeys';
import { isEditableElement } from '../utils/pouchColorHotkeys';
import { GuideContext } from '../contexts/GuideContext';
import './styles/GroupComponent.scss';


interface GroupComponentProps {
  group: Group;
  lastGroupColor: string;
  pouches: Pouch[];
  onMouseDown: (e: React.MouseEvent, targetId?: string, targetType?: 'pouch' | 'group' | 'resize-handle') => void;
  onMouseUp: (e: MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent, targetId?: string, targetType?: 'pouch' | 'group' | 'resize-handle') => void;
  isInitialLoading?: boolean;
  isSheetSwitching?: boolean;
  onMergeGroup?: (groupId: string, isShiftPressed?: boolean, isAltControlPressed?: boolean) => void;
  onExportGroup?: (groupId: string) => void;
  onResetInteraction?: () => void;
  onToggleGroupSign?: (groupId: string) => void;
  onChangeGroupColor?: (groupId: string, isShiftPressed?: boolean, isCtrlOptionPressed?: boolean) => void;
  /** キーボード: GROUP_COLORS の id を直接指定 */
  onSetGroupColorById?: (groupId: string, colorId: string) => void;
  isTouchDevice?: boolean;
  onDeleteGroup?: (groupId: string, isCtrlOptionPressed?: boolean, isShiftPressed?: boolean, isAltControlPressed?: boolean) => void;
  onUpdateGroupTitle?: (groupId: string, title: string) => void;
  onEditConfirm?: () => void;
  onCreateGroupTotalPouch?: (groupId: string, clientX: number, clientY: number) => void; // グループトータルパウチ作成用
  onCheckGroupDragging?: (groupId: string) => boolean; // グループのドラッグ状態をチェック
  allGroups?: Group[]; // マージボタンの条件判定に使用
  isTableSelected?: boolean; // テーブルから選択されているかのフラグ
  sheetTitle?: string; // シート名（TOTAL表示のグループのプレースホルダー用）
  onGroupHover?: (groupId: string | null) => void; // グループホバー時のコールバック
  hoveredGroupId?: string | null; // 現在ホバーされているグループID
  /** ホバー対象の同期判定用（キーホットキーで state の遅延を避ける） */
  hoveredGroupIdRef?: React.MutableRefObject<string | null>;
  gtpHoveredGroupId?: string | null; // GTPホバー時のグループID
  allPouches?: Pouch[]; // 全パウチリスト（グループ外のGTPも含む）
}

interface PendingGroupProps {
  pendingPath: string;
  lastGroupColor: string;
  isTooSmall?: boolean;
}

// グループの主要コンポーネント
export const GroupComponent: React.FC<GroupComponentProps> = ({ 
  group, 
  pouches, 
  onMouseDown,
  onMouseUp,
  onTouchStart,
  isInitialLoading = false,
  isSheetSwitching,
  onMergeGroup = () => {},
  onExportGroup = () => {},
  onResetInteraction = () => {},
  onToggleGroupSign = () => {},
  onChangeGroupColor = () => {},
  onSetGroupColorById = () => {},
  isTouchDevice = false,
  onDeleteGroup = () => {},
  onUpdateGroupTitle = () => {},
  onEditConfirm = () => {},
  onCreateGroupTotalPouch = () => {},
  onCheckGroupDragging = () => false,
  allGroups = [], // デフォルト値として空配列を設定
  isTableSelected = false, // デフォルト値はfalse
  sheetTitle,
  onGroupHover = () => {},
  hoveredGroupId,
  hoveredGroupIdRef,
  gtpHoveredGroupId,
  allPouches = []
}) => {
  const _isGuideEnabled = useContext(GuideContext);
  // パス境界を取得（エラーハンドリング付き）
  const pathBounds = getPathBounds(group.path);
  
  // パス境界の取得に失敗した場合の処理
  if (!pathBounds) {
    console.error('GroupComponent: Failed to get path bounds for group:', group.id, 'Path:', group.path);
    
    // フォールバック: デフォルトの境界を使用
    const fallbackBounds = {
      left: 0,
      top: 0,
      right: 200,
      bottom: 100
    };
    
    
    // 壊れたパスを修復を試行
    try {
      const repairedPath = generateRoundedRectPath(0, 0, 200, 100);
      
      // 修復されたパスでグループを更新（非同期で実行）
      // TODO: onUpdateGroupPath関数を実装する必要がある
      // setTimeout(() => {
      //   if (onUpdateGroupPath) {
      //     onUpdateGroupPath(group.id, repairedPath);
      //   }
      // }, 0);
      
    } catch (repairError) {
      console.error('GroupComponent: Failed to repair path:', repairError);
    }
  }
  
  // タイトル編集用の状態
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(group.title || '');
  const editGuideTimerRef = useRef<number | null>(null);
  const [editGuideTier, setEditGuideTier] = useState<1 | 2>(1);

  const GUIDE_HOVER_DELAY_MS = 2000;
  const groupGuideHoverTimerRef = useRef<number | null>(null);
  const [groupGuideHoverTier, setGroupGuideHoverTier] = useState<0 | 1 | 2>(0);
  const resizeGuideHoverTimerRef = useRef<number | null>(null);
  const resizeGuideHoverDepthRef = useRef(0);
  const [resizeGuideHoverTier, setResizeGuideHoverTier] = useState<0 | 1 | 2>(0);
  
  // IME入力中かどうかの状態
  const [isComposing, setIsComposing] = useState(false);
  
  // タイトル入力欄のref
  const titleInputRef = useRef<HTMLTextAreaElement>(null);
  /** Esc で blur したとき履歴を積まない */
  const titleEscapeCancelRef = useRef(false);
  const wasTitleFocusedOnMouseDown = useRef(false);
  
  // 編集確定フラグ用のref
  const justConfirmedRef = useRef(false);
  const confirmTimeoutRef = useRef<number | null>(null);
  
  // 削除ボタンの長押し検出用のref
  const deleteLongPressTimerRef = useRef<number | null>(null);
  const deletePhaseTimersRef = useRef<number[]>([]);
  const deleteStartTimeRef = useRef<number>(0);
  const deleteCurrentPhaseRef = useRef<number>(0);
  const deleteModifierKeysRef = useRef<{ctrlOption: boolean; shift: boolean; altControl: boolean} | null>(null);

  // マージボタンの長押し検出用のref
  const mergeLongPressTimerRef = useRef<number | null>(null);
  const mergeStartTimeRef = useRef<number>(0);
  const mergeModifierKeysRef = useRef<{
    shift: boolean;
    altControl: boolean;
  } | null>(null);

  // マージフェーズの定義
  const MERGE_PHASES = {
    NORMAL_MERGE: 0,        // 0秒: 通常のマージ（本・内・パが削除プレビュー）
    SHIFT_MERGE: 1          // 1秒: Shift+マージ（内・パが削除プレビュー）
  };

  // すべてのパウチの値が60かチェックする関数
  const shouldShowTimeConversion = useCallback(() => {
    // GTPを除く所属パウチを取得
    const nonGtpPouches = pouches.filter(p => 
      group.pouches?.includes(p.id) && 
      !p._isDeleting && 
      !p.isGroupTotalPouch &&
      !allGroups.some(g => g.color === 'mute' && g.pouches?.includes(p.id))
    );
    
    // 所属パウチがない場合は時刻変換なし
    if (nonGtpPouches.length === 0) {
      return false;
    }
    
    // すべてのパウチのisTimeプロパティがtrueかチェック
    return nonGtpPouches.every(p => p.isTime === true);
  }, [group.pouches, pouches, allGroups]);

  /** グループ SUBTOTAL/TOTAL（ミュート除外・GTP 含む）— シート合計と同じ寄与定義 */
  const groupSubtotalContribution = useMemo(() => {
    const groupPouches = pouches.filter(p =>
      group.pouches?.includes(p.id) &&
      !p._isDeleting &&
      !allGroups.some(g => g.color === 'mute' && g.pouches?.includes(p.id))
    );
    return groupPouches.reduce((total, pouch) => total + getPouchEffectiveContribution(pouch), 0);
  }, [group.pouches, pouches, allGroups]);

  // TOTAL表示判定を遅延更新で確実に最新状態で判定
  const [isTotalGroup, setIsTotalGroup] = useState(shouldShowTotalLabel(group, allGroups || [], pouches));
  
  useEffect(() => {
    // 遅延させてから判定を更新（確実に最新の状態を反映）
    const timeoutId = setTimeout(() => {
      setIsTotalGroup(shouldShowTotalLabel(group, allGroups || [], pouches));
    }, 50); // 50ms遅延でグループ操作完了後に判定更新
    
    return () => clearTimeout(timeoutId);
  }, [group.id, group.pouches, group.title, allGroups, pouches]); // リサイズ終了時（pouches更新時）に再判定

  // z-index更新用のuseEffect
  useEffect(() => {
    if (allGroups && allGroups.length > 1) {
      const zIndexManager = GroupZIndexManager.getInstance();
      const allGroupIds = allGroups.map(g => g.id);
      
      // 遅延実行でz-indexを更新（DOM操作が完了してから）
      const timeoutId = setTimeout(() => {
        zIndexManager.updateAllGroupZIndices(allGroupIds);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        // このグループのキャッシュをクリア
        zIndexManager.clearGroupCache(group.id);
      };
    }
  }, [group.id, group.path, allGroups]);

  // グローバルな修飾キー状態を追跡するためのuseEffect
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        (window as any).__ctrlKeyPressed = true;
      } else if (e.key === 'Alt') {
        (window as any).__altKeyPressed = true;
      } else if (e.key === 'Shift') {
        (window as any).__shiftKeyPressed = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        (window as any).__ctrlKeyPressed = false;
      } else if (e.key === 'Alt') {
        (window as any).__altKeyPressed = false;
      } else if (e.key === 'Shift') {
        (window as any).__shiftKeyPressed = false;
      }
    };

    // 初期化
    (window as any).__ctrlKeyPressed = false;
    (window as any).__altKeyPressed = false;
    (window as any).__shiftKeyPressed = false;

    // イベントリスナーを追加
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    // クリーンアップ
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);
  
  // 削除フェーズの定義
  const DELETE_PHASES = {
    MAIN_ONLY: 0,        // 0秒: 本削除
    CONTAINED_ONLY: 1,   // 1秒: 内削除
    CONTAINED_WITH_POUCHES: 2, // 2秒: 内・パ削除
    MAIN_WITH_CONTAINED: 3,    // 3秒: 本・内削除（パ復活）
    ALL_DELETE: 4        // 4秒: 本・内・パ削除
  };
  
  
  // 削除ボタンのtitle生成関数
  const getDeleteButtonTooltipHtml = () => {
    if (!(window as any).__isGuideEnabled && (window as any).__isGuideEnabled !== undefined) return '';
    const keys = getModifierKeys();
    const s = (ch: string) => `<span style="color:#808080;font-size:1.3em;line-height:1;vertical-align:-0.05em">${ch}</span>`;
    const line = (key: string, desc: string) => `${formatTooltip(key)} <span class="tt-desc">${desc}</span>`;
    return [
      line('Click', `Delete Group ${s('■')} (this frame)`),
      line('Long Press', 'Delete Group (progressive)'),
      line(`${keys.ctrlOption}+Click`, `Delete Group ${s('■▣⛬')} (all)`),
      line(`${keys.shift}+${keys.ctrlOption}+Click`, `Delete Group ${s('■▣')} (groups)`),
      line(`${keys.altControl}+Click`, `Delete Group ${s('▣⛬')} (inner groups &amp; pouches)`),
      line(`${keys.shift}+${keys.altControl}+Click`, `Delete Group ${s('▣')} (inner groups)`),
    ].join('<br/>');
  };
  
  
  // マージ用のプレビュー表示関数
  const applyMergePhase = useCallback((phase: number, save: boolean = false) => {
    if (!save) {
      
      // まず、すべてのプレビュークラスをクリア
      const allGroupElements = document.querySelectorAll('[data-group-id]');
      allGroupElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      const allPouchElements = document.querySelectorAll('[data-pouch-id]');
      allPouchElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
    }

    const targets = checkDeleteTargets();
    
    switch (phase) {
      case MERGE_PHASES.NORMAL_MERGE:
        if (!save) {
          // 本・内・パが削除プレビュー
          const groupElement = document.querySelector(`[data-group-id="${group.id}"]`);
          if (groupElement) {
            groupElement.classList.add('delete-preview');
          }
          
          // 内包グループのプレビュー
          const containedGroups = getContainedGroups(group, allGroups);
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.add('delete-preview');
            }
          });
          
          // 所属パウチのプレビュー
          const pouchesInGroup = pouches.filter(p => {
            return group.pouches && group.pouches.includes(p.id);
          });
          pouchesInGroup.forEach(pouch => {
            const pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            if (pouchElement) {
              pouchElement.classList.add('delete-preview');
            }
          });
        } else {
          // 実際のマージ処理（通常のマージ）
          onMergeGroup(group.id, false, false);
        }
        break;
        
      case MERGE_PHASES.SHIFT_MERGE:
        if (!save) {
          // 内・パが削除プレビュー（本は除外）
          const containedGroups = getContainedGroups(group, allGroups);
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.add('delete-preview');
            }
          });
          
          // 所属パウチのプレビュー
          const pouchesInGroup = pouches.filter(p => {
            return group.pouches && group.pouches.includes(p.id);
          });
          pouchesInGroup.forEach(pouch => {
            const pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            if (pouchElement) {
              pouchElement.classList.add('delete-preview');
            }
          });
        } else {
          // 実際のマージ処理（Shift+マージ）- 本体グループは削除しない
          onMergeGroup(group.id, true, false);
        }
        break;
    }
  }, [group, pouches, allGroups, onMergeGroup]);
  
  // マージボタンが無効化されるべきかを判断
  const isMergeDisabled = useMemo(() => {
    return isGroupMergeDisabled(group, pathBounds, allGroups, pouches);
  }, [group, pathBounds, allGroups, pouches, group._isDragging]);

  // マージ用のタイマークリア関数
  const clearMergeTimers = useCallback(() => {
    if (mergeLongPressTimerRef.current) {
      window.clearTimeout(mergeLongPressTimerRef.current);
      mergeLongPressTimerRef.current = null;
    }
    mergeStartTimeRef.current = 0;
    mergeModifierKeysRef.current = null;
  }, []);

  // マージ用の共通開始ハンドラー
  const handleMergeStart = useCallback((e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();
    e.preventDefault();
    
    if (isMergeDisabled) {
      return;
    }
    
    // 修飾キーが押されているかチェック
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    let isShiftPressed = false;
    let isAltControlPressed = false;
    
    if (eventType === 'mouse') {
      const mouseEvent = e as React.MouseEvent;
      isShiftPressed = mouseEvent.shiftKey || false;
      isAltControlPressed = isMac ? mouseEvent.ctrlKey : mouseEvent.altKey;
    } else {
      isShiftPressed = (window as any).__shiftKeyPressed || false;
      isAltControlPressed = isMac ? (window as any).__ctrlKeyPressed : (window as any).__altKeyPressed;
    }
    
    // 修飾キー状態を保存
    mergeModifierKeysRef.current = {
      shift: isShiftPressed,
      altControl: isAltControlPressed
    };
    mergeStartTimeRef.current = Date.now();
    
    // 修飾キーが押されている場合は、開始時に修飾キー相当のプレビューを表示
    if (mergeModifierKeysRef.current) {
      const { shift, altControl } = mergeModifierKeysRef.current;
      if (shift || altControl) {
        applyMergePhase(MERGE_PHASES.SHIFT_MERGE, false);
        // 修飾キーが押されていても長押しタイマーを開始（フェーズ2の実行のため）
        mergeLongPressTimerRef.current = window.setTimeout(() => {
          // フェーズ2の実行処理を呼ぶ
          applyMergePhase(MERGE_PHASES.SHIFT_MERGE, true);
        }, 1000);
        return;
      }
    }
    
    // 修飾キーが押されていない場合は、通常の処理
    applyMergePhase(MERGE_PHASES.NORMAL_MERGE, false);
    
    // 長押し検出タイマーを開始
    mergeLongPressTimerRef.current = window.setTimeout(() => {
      // すべてのプレビュークラスをクリア
      const allGroupElements = document.querySelectorAll('[data-group-id]');
      allGroupElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      const allPouchElements = document.querySelectorAll('[data-pouch-id]');
      allPouchElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      
      // フェーズ1（Shift+マージ）のプレビューを表示
      applyMergePhase(MERGE_PHASES.SHIFT_MERGE, false);
    }, 1000);
  }, [isMergeDisabled, applyMergePhase]);

  // マージ用の共通終了ハンドラー
  const handleMergeEnd = useCallback((e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();
    // タイマーをクリア
    if (mergeLongPressTimerRef.current) {
      window.clearTimeout(mergeLongPressTimerRef.current);
      mergeLongPressTimerRef.current = null;
    }
    
    if (mergeStartTimeRef.current > 0) {
      const holdDuration = Date.now() - mergeStartTimeRef.current;
      
      if (holdDuration >= 1000) {
        // 長押し検出済み
        
        // 修飾キーが押されている場合は、修飾キー相当のフェーズで保存
        if (mergeModifierKeysRef.current) {
          const { shift, altControl } = mergeModifierKeysRef.current;
          if (shift || altControl) {
            applyMergePhase(MERGE_PHASES.SHIFT_MERGE, true);
            clearMergeTimers();
            return;
          }
        }
        
        // 修飾キーが押されていない場合は、フェーズ1で保存
        applyMergePhase(MERGE_PHASES.SHIFT_MERGE, true);
      } else {
        // 短いクリックの場合、修飾キーの状態に応じて処理
        if (mergeModifierKeysRef.current) {
          const { shift, altControl } = mergeModifierKeysRef.current;
          if (shift || altControl) {
            // 修飾キーが押されている場合は、Shift+マージ
            applyMergePhase(MERGE_PHASES.SHIFT_MERGE, true);
          } else {
            // 修飾キーが押されていない場合は、通常のマージ
            applyMergePhase(MERGE_PHASES.NORMAL_MERGE, true);
          }
        } else {
          // 通常のマージ処理
          applyMergePhase(MERGE_PHASES.NORMAL_MERGE, true);
        }
      }
    } else {
      // 通常のクリック処理（短いクリック）
      applyMergePhase(MERGE_PHASES.NORMAL_MERGE, true);
    }
    
    clearMergeTimers();
  }, [applyMergePhase, clearMergeTimers]);

  // マージ用のキャンセルハンドラー
  const handleMergeCancel = useCallback(() => {
    clearMergeTimers();
    
    // プレビュークラスをクリア
    const allGroupElements = document.querySelectorAll('[data-group-id]');
    allGroupElements.forEach(element => {
      element.classList.remove('delete-preview');
    });
    const allPouchElements = document.querySelectorAll('[data-pouch-id]');
    allPouchElements.forEach(element => {
      element.classList.remove('delete-preview');
    });
  }, [clearMergeTimers]);

  // カラー変更ボタンの長押し検出用のref
  const colorLongPressTimerRef = useRef<number | null>(null);
  const colorStartTimeRef = useRef<number>(0);
  const colorModifierKeysRef = useRef<{
    shift: boolean;
    ctrlOption: boolean;
  } | null>(null);

  // カラー変更用のタイマークリア関数
  const clearColorTimers = useCallback(() => {
    if (colorLongPressTimerRef.current) {
      window.clearTimeout(colorLongPressTimerRef.current);
      colorLongPressTimerRef.current = null;
    }
    colorStartTimeRef.current = 0;
    colorModifierKeysRef.current = null;
  }, []);

  // カラー変更用の共通開始ハンドラー
  const handleColorStart = useCallback((e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();
    e.preventDefault();
    
    
    // 修飾キーが押されているかチェック
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    let isShiftPressed = false;
    let isCtrlOptionPressed = false;
    
    if (eventType === 'mouse') {
      const mouseEvent = e as React.MouseEvent;
      isShiftPressed = mouseEvent.shiftKey || false;
      isCtrlOptionPressed = isMac ? mouseEvent.altKey : mouseEvent.ctrlKey;
    } else {
      isShiftPressed = (window as any).__shiftKeyPressed || false;
      isCtrlOptionPressed = isMac ? (window as any).__altKeyPressed : (window as any).__ctrlKeyPressed;
    }
    
    
    // 修飾キー状態を保存
    colorModifierKeysRef.current = {
      shift: isShiftPressed,
      ctrlOption: isCtrlOptionPressed
    };
    colorStartTimeRef.current = Date.now();
    
    // 修飾キーが押されている場合は、開始時に修飾キー相当の動作を実行
    if (colorModifierKeysRef.current) {
      const { shift, ctrlOption } = colorModifierKeysRef.current;
      if (shift || ctrlOption) {
        if (shift) {
          // Shift+クリック: 逆ローテーション
          onChangeGroupColor(group.id, true, false);
        } else if (ctrlOption) {
          // Ctrl/Option+クリック: ミュート/元に戻す
          onChangeGroupColor(group.id, false, true);
        }
        return; // 修飾キーが押されている場合は長押しタイマーを開始しない
      }
    }
    
    // 修飾キーが押されていない場合は、長押し検出タイマーを開始
    colorLongPressTimerRef.current = window.setTimeout(() => {
      // 修飾キーなし+長押し: ミュート/元に戻す
      onChangeGroupColor(group.id, false, true);
    }, 1000);
  }, [group.id, onChangeGroupColor]);

  // カラー変更用の共通終了ハンドラー
  const handleColorEnd = useCallback((e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();
    
    
    // タイマーをクリア
    if (colorLongPressTimerRef.current) {
      window.clearTimeout(colorLongPressTimerRef.current);
      colorLongPressTimerRef.current = null;
    }
    
    // 修飾キーが押されていた場合は、既に開始時に処理済みなので何もしない
    if (colorModifierKeysRef.current) {
      const { shift, ctrlOption } = colorModifierKeysRef.current;
      if (shift || ctrlOption) {
        clearColorTimers();
        return;
      }
    }
    
    if (colorStartTimeRef.current > 0) {
      const holdDuration = Date.now() - colorStartTimeRef.current;
      
      if (holdDuration >= 1000) {
        // 長押し検出済み - 既に長押しタイマーで処理済み
      } else {
        // 短いクリックの場合、通常のカラー変更処理
        onChangeGroupColor(group.id, false, false);
      }
    } else {
      // 通常のクリック処理（短いクリック）
      onChangeGroupColor(group.id, false, false);
    }
    
    clearColorTimers();
  }, [group.id, onChangeGroupColor, clearColorTimers]);

  // カラー変更用のキャンセルハンドラー
  const handleColorCancel = useCallback(() => {
    clearColorTimers();
  }, [clearColorTimers]);

  const getGroupColorTooltipHtml = () => {
    if (!(window as any).__isGuideEnabled && (window as any).__isGuideEnabled !== undefined) return '';
    const MUTE_COLOR = '#666666';
    const allColors = [...GROUP_COLORS.map(c => ({ id: c.id, value: c.value })), { id: 'mute', value: MUTE_COLOR }];
    const currentColor = group.color || GROUP_COLORS[0].id;
    const currentIndex = allColors.findIndex(c => c.id === currentColor);
    const nextIndex = (currentIndex + 1) % allColors.length;
    const prevIndex = (currentIndex - 1 + allColors.length) % allColors.length;
    const sym = (color: string, ch: string) => `<span style="color:${color};font-size:1.3em;line-height:1;vertical-align:-0.05em">${ch}</span>`;
    const colorLabel = (idx: number) => allColors[idx].id === 'mute'
      ? { isMuteTarget: true, html: `Enable Mute ${sym(MUTE_COLOR, '▩')}` }
      : { isMuteTarget: false, html: `Change Group Color to ${sym(allColors[idx].value, '■')}` };
    const keys = getModifierKeys();
    const isMute = currentColor === 'mute';
    const muteLine = isMute
      ? `Disable Mute ${sym(GROUP_COLORS.find(c => c.id === (group.originalColor || 'yellow'))?.value || GROUP_COLORS[0].value, '■')}`
      : `Enable Mute ${sym(MUTE_COLOR, '▩')}`;
    return `${formatTooltip('Click')} <span class="tt-desc">${colorLabel(nextIndex).html}</span><br/>${formatTooltip(keys.shift)}${formatTooltip('Click')} <span class="tt-desc">${colorLabel(prevIndex).html}</span><br/>${formatTooltip(keys.ctrlOption)}${formatTooltip('Click / Long Press')} <span class="tt-desc">${muteLine}</span>`;
  };

  const groupGuideMovementFullHtml = useMemo(() => {
    const keys = getModifierKeys();
    return formatTooltip(
      [
        'Drag: Move Group',
        `${keys.shift}+Drag: Move Group Frame Only`,
        'Long Press Drag: Clone Group',
        `${keys.shift}+Long Press Drag: Clone Group Frame Only`,
        `${keys.ctrlOption}+Drag: Clone Group`,
        `${keys.shift}+${keys.ctrlOption}+Drag: Clone Group Frame Only`,
      ].join('\n'),
    );
  }, []);

  const groupGuideColorExtrasHtml = useMemo(() => {
    const MUTE_COLOR = '#666666';
    const allColors = [...GROUP_COLORS.map(c => ({ id: c.id, value: c.value })), { id: 'mute', value: MUTE_COLOR }];
    const currentColor = group.color || GROUP_COLORS[0].id;
    const currentIndex = allColors.findIndex(c => c.id === currentColor);
    const nextIndex = (currentIndex + 1) % allColors.length;
    const prevIndex = (currentIndex - 1 + allColors.length) % allColors.length;
    const sym = (color: string, ch: string) => `<span style="color:${color};font-size:1.3em;line-height:1;vertical-align:-0.05em">${ch}</span>`;
    const colorLabel = (idx: number) =>
      allColors[idx].id === 'mute'
        ? `Enable Mute ${sym(MUTE_COLOR, '▩')}`
        : `Change Group Color to ${sym(allColors[idx].value, '■')}`;
    const keys2 = getModifierKeys();
    const rotateNext = `${formatTooltip('C')} <span class="tt-desc">${colorLabel(nextIndex)}</span>`;
    const rotatePrev = `${formatTooltip(keys2.shift)}${formatTooltip('C')} <span class="tt-desc">${colorLabel(prevIndex)}</span>`;
    const colorCircle = (idx: number) => {
      const c = GROUP_COLORS[idx];
      return `<span style="color:${c.value};font-size:1.3em;line-height:1;vertical-align:-0.05em">■</span>`;
    };
    const directRow = `<span class="tt-desc">${GROUP_COLOR_HOTKEY_DISPLAY_ORDER.map(({ letter, colorId }) => {
      if (colorId === 'mute') {
        return `<kbd>${letter}</kbd><span style="color:${MUTE_COLOR};font-size:1.3em;line-height:1;vertical-align:-0.05em">▩</span>`;
      }
      const idx = GROUP_COLORS.findIndex(c => c.id === colorId);
      const circ = idx >= 0 ? colorCircle(idx) : '';
      return `<kbd>${letter}</kbd>${circ}`;
    }).join(' ')}</span>`;
    return `${rotateNext}<br/>${rotatePrev}<br/>${directRow}`;
  }, [group.color, group.originalColor]);

  /** リサイズハンドル tier2 と同じキーボード追記（各ボタン・サブトータルにも使用） */
  const groupHoverTier2KeyboardExtrasHtml = useMemo(() => {
    if (isTouchDevice) return '';
    if (!_isGuideEnabled) {
      return `${formatGroupGuideEditPcExtrasHtml()}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
    }
    return `${formatGroupGuideEditPcExtrasHtml()}<br/>${groupGuideColorExtrasHtml}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
  }, [isTouchDevice, _isGuideEnabled, groupGuideColorExtrasHtml]);

  const groupExportTooltipHtml = useMemo(() => {
    const p1 = formatTooltip('Click: Export Summary Text');
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    return `${p1}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml]);

  const groupToggleSignTooltipHtml = useMemo(() => {
    const p1 = formatTooltip('Click: Toggle +/- for All Pouches in Group');
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    const escLine = `${formatTooltip('Esc')} <span class="tt-desc">Toggle +/- for All Pouches in Group</span>`;
    return `${p1}<br/>${escLine}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml]);

  const groupColorButtonTooltipHtml = useMemo(() => {
    const p1 = getGroupColorTooltipHtml();
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    if (!p1) return groupHoverTier2KeyboardExtrasHtml;
    return `${p1}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml, group.color, group.originalColor]);

  const groupMergeTooltipHtml = useMemo(() => {
    if (!(window as any).__isGuideEnabled && (window as any).__isGuideEnabled !== undefined) {
      if (groupGuideHoverTier < 2 || isTouchDevice) return '';
      return groupHoverTier2KeyboardExtrasHtml;
    }
    const s = (ch: string) => `<span style="color:#808080;font-size:1.3em;line-height:1;vertical-align:-0.05em">${ch}</span>`;
    const line = (key: string, desc: string) => `${formatTooltip(key)} <span class="tt-desc">${desc}</span>`;
    const p1 = [
      line('Click', `Merge ${s('■▣⛬')} All`),
      line('Long Press', 'Progressive Merge'),
      line(`${getModifierKeys().shift}+Click`, `Merge ${s('▣⛬')} Inner Groups &amp; Pouches`),
    ].join('<br/>');
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    return `${p1}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml]);

  const groupDeleteButtonTooltipHtml = useMemo(() => {
    const p1 = getDeleteButtonTooltipHtml();
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    if (!p1) return groupHoverTier2KeyboardExtrasHtml;
    return `${p1}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml]);

  const groupAddGtpTooltipHtml = useMemo(() => {
    const p1 = formatTooltip('Click: Add Group Total Pouch');
    if (groupGuideHoverTier < 2 || isTouchDevice) return p1;
    return `${p1}<br/>${groupHoverTier2KeyboardExtrasHtml}`;
  }, [groupGuideHoverTier, isTouchDevice, groupHoverTier2KeyboardExtrasHtml]);

  const groupContainerTooltipHtml = useMemo(() => {
    if (isTitleEditing) return '';
    const phase1 = formatTooltip('Double Click: Create Pouch\nDrag: Move Group');
    if (groupGuideHoverTier < 2) {
      return phase1;
    }
    if (isTouchDevice) {
      return groupGuideMovementFullHtml;
    }
    const escGroupToggle =
      group.pouches && group.pouches.length > 0
        ? `<br/>${formatTooltip('Esc')} <span class="tt-desc">Toggle +/- for All Pouches in Group</span>`
        : '';
    if (!_isGuideEnabled) {
      return `${groupGuideMovementFullHtml}<br/>${formatGroupGuideEditPcExtrasHtml()}${escGroupToggle}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
    }
    return `${groupGuideMovementFullHtml}<br/>${formatGroupGuideEditPcExtrasHtml()}${escGroupToggle}<br/>${groupGuideColorExtrasHtml}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
  }, [
    isTitleEditing,
    groupGuideHoverTier,
    isTouchDevice,
    groupGuideMovementFullHtml,
    groupGuideColorExtrasHtml,
    _isGuideEnabled,
    group.pouches,
  ]);

  const groupResizeTooltipHtml = useMemo(() => {
    if (isTitleEditing) return '';
    const r1 = formatTooltip('Drag: Resize Group');
    if (resizeGuideHoverTier < 2) {
      return r1;
    }
    if (isTouchDevice) {
      return r1;
    }
    if (!_isGuideEnabled) {
      return `${r1}<br/>${formatGroupGuideEditPcExtrasHtml()}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
    }
    return `${r1}<br/>${formatGroupGuideEditPcExtrasHtml()}<br/>${groupGuideColorExtrasHtml}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
  }, [isTitleEditing, resizeGuideHoverTier, isTouchDevice, groupGuideColorExtrasHtml, _isGuideEnabled]);

  /** グループタイトル欄ホバー: 初期は Click のみ、2 秒後はドラッグ＋Edit→色→Delete */
  const groupTitleLongHoverExtrasHtml = useMemo(() => {
    if (!_isGuideEnabled || isTouchDevice) return '';
    return [
      formatTooltip('Drag: Move Group'),
      formatGroupGuideEditPcExtrasHtml(),
      groupGuideColorExtrasHtml,
      formatGroupGuideDeletePcExtrasHtml(),
    ].join('<br/>');
  }, [_isGuideEnabled, isTouchDevice, groupGuideColorExtrasHtml]);

  const groupTitleTooltipHtml = useMemo(() => {
    if (isTitleEditing) return '';
    const phase1 = formatTooltip('Click: Edit Group Title');
    if (groupGuideHoverTier < 2) {
      return phase1;
    }
    if (isTouchDevice) {
      return phase1;
    }
    if (!_isGuideEnabled) {
      return `${phase1}<br/>${formatGroupGuideEditPcExtrasHtml()}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
    }
    if (groupTitleLongHoverExtrasHtml) {
      return `${phase1}<br/>${groupTitleLongHoverExtrasHtml}`;
    }
    return phase1;
  }, [isTitleEditing, groupGuideHoverTier, isTouchDevice, groupTitleLongHoverExtrasHtml, _isGuideEnabled]);

  /** グループタイトル編集中（パウチと同様: Enter/Esc → 2 秒後に色） */
  const groupEditModeTooltipHtml = useMemo(() => {
    if (!isTitleEditing) return '';
    const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const enterHint = `${formatTooltip('Enter')} <span class="tt-desc">${esc('Commit Title')}</span>`;
    const escHint = `${formatTooltip('Esc')} <span class="tt-desc">${esc('Cancel')}</span>`;
    const tier1 = `${enterHint}<br/>${escHint}`;
    if (editGuideTier < 2) {
      return tier1;
    }
    if (isTouchDevice) {
      return tier1;
    }
    if (groupGuideColorExtrasHtml) {
      return `${tier1}<br/>${groupGuideColorExtrasHtml}`;
    }
    return tier1;
  }, [isTitleEditing, editGuideTier, isTouchDevice, groupGuideColorExtrasHtml]);

  useEffect(() => {
    if (isTouchDevice) {
      return;
    }
    if (isTitleEditing) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      const activeId = hoveredGroupIdRef?.current ?? hoveredGroupId;
      if (activeId !== group.id) {
        return;
      }
      if (group._isDragging) {
        return;
      }
      if (document.activeElement === titleInputRef.current) {
        return;
      }
      if (isComposing) {
        return;
      }
      if (isEditableElement(e.target as Element)) {
        return;
      }
      if (e.key === 'F2' || e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        setIsTitleEditing(true);
        requestAnimationFrame(() => {
          titleInputRef.current?.focus();
          titleInputRef.current?.select();
        });
        return;
      }
      const action = resolveGroupColorHotkey(e);
      if (!action) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (action.type === 'rotate') {
        onChangeGroupColor(group.id, action.reverse, false);
        return;
      }
      if (action.type === 'muteToggle') {
        onChangeGroupColor(group.id, false, true);
        return;
      }
      if (action.type === 'direct') {
        onSetGroupColorById(group.id, action.colorId);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isTouchDevice,
    hoveredGroupId,
    hoveredGroupIdRef,
    group.id,
    group._isDragging,
    isTitleEditing,
    isComposing,
    onChangeGroupColor,
    onSetGroupColorById,
  ]);

  // PC・グループホバー時: Delete / Backspace = 削除ボタンの短クリックと同じ修飾キー（編集中・編集系フォーカス時は無効）
  useEffect(() => {
    if (isTouchDevice) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.repeat || (e.key !== 'Delete' && e.key !== 'Backspace')) {
        return;
      }
      if (isComposing) {
        return;
      }
      const activeId = hoveredGroupIdRef?.current ?? hoveredGroupId;
      if (activeId !== group.id) {
        return;
      }
      if (group._isDragging) {
        return;
      }
      if (isTitleEditing) {
        return;
      }
      if (document.activeElement === titleInputRef.current) {
        return;
      }
      if (isEditableElement(document.activeElement)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isCtrlOptionPressed = isMac ? e.altKey : e.ctrlKey;
      const isShiftPressed = e.shiftKey || false;
      const isAltControlPressed = isMac ? e.ctrlKey : e.altKey;
      onDeleteGroup(group.id, isCtrlOptionPressed, isShiftPressed, isAltControlPressed);
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isTouchDevice,
    hoveredGroupId,
    hoveredGroupIdRef,
    group.id,
    group._isDragging,
    isTitleEditing,
    isComposing,
    onDeleteGroup,
  ]);

  // 削除対象の存在チェック
  const checkDeleteTargets = useCallback(() => {
    // deleteGroupWithContentsと同じ方法でパウチの所属をチェック
    const pouchesInGroup = pouches.filter(p => {
      // 方法1: グループのpouchesプロパティをチェック
      const isInGroupPouches = group.pouches && group.pouches.includes(p.id);
      // groupIdsは廃止済み
      return isInGroupPouches;
    });
    
    // 正しい内包グループのチェック
    const containedGroups = getContainedGroups(group, allGroups);
    
    
    return {
      hasContainedGroups: containedGroups.length > 0,
      hasPouches: pouchesInGroup.length > 0,
      canDeleteMain: true,
      canDeleteContained: containedGroups.length > 0,
      canDeletePouches: pouchesInGroup.length > 0
    };
  }, [group, pouches, allGroups]);
  
  // 削除フェーズの適用（可視化のみ）
  const applyDeletePhase = useCallback((phase: number, save: boolean = false) => {
    deleteCurrentPhaseRef.current = phase;
    
    // 削除状態の可視化（animationflags処理）
    // 実際の削除は行わず、画面反映のみ
    // プレビューフェーズのみログ
    if (!save) {
      const phaseNames = {
        [DELETE_PHASES.MAIN_ONLY]: 'MAIN_ONLY (本削除)',
        [DELETE_PHASES.CONTAINED_ONLY]: 'CONTAINED_ONLY (内削除)', 
        [DELETE_PHASES.CONTAINED_WITH_POUCHES]: 'CONTAINED_WITH_POUCHES (内・パ削除)',
        [DELETE_PHASES.MAIN_WITH_CONTAINED]: 'MAIN_WITH_CONTAINED (本・内削除)',
        [DELETE_PHASES.ALL_DELETE]: 'ALL_DELETE (本・内・パ削除)'
      };
    }
    
    // 削除状態の可視化
    if (!save) {
      // 可視化のみ（保存しない）
      
      // まず、すべてのプレビュークラスをクリア
      const allGroupElements = document.querySelectorAll('[data-group-id]');
      allGroupElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      const allPouchElements = document.querySelectorAll('[data-pouch-id]');
      allPouchElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      
      const targets = checkDeleteTargets();
      // checkDeleteTargetsと同じ方法でパウチを検出
      const pouchesInGroup = pouches.filter(p => {
        const isInGroupPouches = group.pouches && group.pouches.includes(p.id);
        // groupIdsは廃止済み
        return isInGroupPouches;
      });
      const containedGroups = getContainedGroups(group, allGroups);
      
      // 削除対象の要素にクラスを追加/削除
      switch (phase) {
        case DELETE_PHASES.MAIN_ONLY:
          // 0秒: 本削除
          const groupElement = document.querySelector(`[data-group-id="${group.id}"]`);
          if (groupElement) {
            groupElement.classList.add('delete-preview');
          }
          // 他の要素は通常状態に戻す
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.remove('delete-preview');
            }
          });
          pouchesInGroup.forEach(pouch => {
            let pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
            if (!pouchElement) {
              pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            }
            if (pouchElement) {
              pouchElement.classList.remove('delete-preview');
            }
          });
          break;
          
        case DELETE_PHASES.CONTAINED_ONLY:
          // 1秒: 内削除
          // 本グループの削除プレビューを削除
          const groupElement1 = document.querySelector(`[data-group-id="${group.id}"]`);
          if (groupElement1) {
            groupElement1.classList.remove('delete-preview');
          }
          // 内包グループを削除状態に
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.add('delete-preview');
            }
          });
          break;
          
        case DELETE_PHASES.CONTAINED_WITH_POUCHES:
          // 2秒: 内・パ削除
          // 内包グループを削除状態に
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.add('delete-preview');
            }
          });
          // パウチを削除状態に
          pouchesInGroup.forEach(pouch => {
            // 複数のセレクターを試す
            let pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
            if (!pouchElement) {
              pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            }
            if (pouchElement) {
              pouchElement.classList.add('delete-preview');
            }
          });
          break;
          
        case DELETE_PHASES.MAIN_WITH_CONTAINED:
          // 3秒: 本・内削除（パ復活）
          const groupElement2 = document.querySelector(`[data-group-id="${group.id}"]`);
          if (groupElement2) {
            groupElement2.classList.add('delete-preview');
          }
          // 内包グループを削除状態に
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.add('delete-preview');
            }
          });
          // パウチは通常状態に戻す
          pouchesInGroup.forEach(pouch => {
            let pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
            if (!pouchElement) {
              pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            }
            if (pouchElement) {
              pouchElement.classList.remove('delete-preview');
            }
          });
          break;
          
        case DELETE_PHASES.ALL_DELETE:
          // 4秒: 本・内・パ削除
          // 本グループを削除状態に
          const groupElement4 = document.querySelector(`[data-group-id="${group.id}"]`);
          if (groupElement4) {
            groupElement4.classList.add('delete-preview');
          }
          // 内包グループを削除状態に
          containedGroups.forEach(containedGroup => {
            const containedElement = document.querySelector(`[data-group-id="${containedGroup.id}"]`);
            if (containedElement) {
              containedElement.classList.add('delete-preview');
            }
          });
          // パウチを削除状態に
          pouchesInGroup.forEach(pouch => {
            let pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouch.id}"]`);
            if (!pouchElement) {
              pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            }
            if (pouchElement) {
              pouchElement.classList.add('delete-preview');
            }
          });
          break;
      }
    }
    
    if (save) {
      // 実際の削除処理を実行
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      let isCtrlOptionPressed = false;
      let isShiftPressed = false;
      let isAltControlPressed = false;
      
      switch (phase) {
        case DELETE_PHASES.MAIN_ONLY:
          // 0秒: 本削除（通常削除）
          break;
        case DELETE_PHASES.CONTAINED_ONLY:
          // 1秒: 内削除（Shift+Alt/Control+削除と同じ）
          isShiftPressed = true;
          isAltControlPressed = true; // Mac: Ctrl, Windows: Alt
          break;
        case DELETE_PHASES.CONTAINED_WITH_POUCHES:
          // 2秒: 内・パ削除（Alt/Control+削除と同じ）
          isAltControlPressed = true; // Mac: Ctrl, Windows: Alt
          break;
        case DELETE_PHASES.MAIN_WITH_CONTAINED:
          // 3秒: 本・内削除（Shift+Ctrl/Option+削除と同じ）
          isShiftPressed = true;
          isCtrlOptionPressed = true; // Mac: Option, Windows: Ctrl
          break;
        case DELETE_PHASES.ALL_DELETE:
          // 4秒: 本・内・パ削除（Ctrl/Option+削除と同じ）
          isCtrlOptionPressed = true; // Mac: Option, Windows: Ctrl
          break;
      }
      
      // 実際の削除処理実行
      
      onDeleteGroup(group.id, isCtrlOptionPressed, isShiftPressed, isAltControlPressed);
    }
  }, [group.id, onDeleteGroup]);
  
  // 削除タイマーのクリア
  const clearDeleteTimers = useCallback(() => {
    if (deleteLongPressTimerRef.current !== null) {
      window.clearTimeout(deleteLongPressTimerRef.current);
      deleteLongPressTimerRef.current = null;
    }
    
    deletePhaseTimersRef.current.forEach(timerId => {
      window.clearTimeout(timerId);
    });
    deletePhaseTimersRef.current = [];
    
    deleteStartTimeRef.current = 0;
    deleteCurrentPhaseRef.current = 0;
    deleteModifierKeysRef.current = null;
    
    // 削除プレビューのクラスをクリア
    const allElements = document.querySelectorAll('.delete-preview');
    allElements.forEach(element => {
      element.classList.remove('delete-preview');
    });
  }, []);

  // このグループに関連するGTPのIDを取得（グループ外のGTPも含む）
  const relatedGtpIds = useMemo(() => {
    return allPouches
      .filter(pouch => pouch.isGroupTotalPouch && pouch.referencedGroupId === group.id)
      .map(pouch => pouch.id);
  }, [allPouches, group.id, group._isDragging]);


  // ホバー状態のクラス名を生成
  const groupClassName = useMemo(() => {
    const classes = [];
    
    // このグループがホバーされている場合
    if (hoveredGroupId === group.id) {
      classes.push('group-hovered');
    }
    
    // ドラッグ中のクラスは手動で管理するため、ここでは追加しない
    
    // GTPホバー時のクラスはActiveSheetComponentから直接適用されるため、ここでは処理しない
    
    return classes.join(' ');
  }, [hoveredGroupId, group.id]);

  
  


  


  // ホバーイベントハンドラー（操作ガイド: 2 秒で tier 2）
  const handleMouseEnter = useCallback(() => {
    onGroupHover(group.id);
    setGroupGuideHoverTier(1);
    if (groupGuideHoverTimerRef.current !== null) {
      window.clearTimeout(groupGuideHoverTimerRef.current);
    }
    groupGuideHoverTimerRef.current = window.setTimeout(() => {
      setGroupGuideHoverTier(2);
      groupGuideHoverTimerRef.current = null;
    }, GUIDE_HOVER_DELAY_MS);
  }, [onGroupHover, group.id]);

  const handleMouseLeave = useCallback(() => {
    if (groupGuideHoverTimerRef.current !== null) {
      window.clearTimeout(groupGuideHoverTimerRef.current);
      groupGuideHoverTimerRef.current = null;
    }
    setGroupGuideHoverTier(0);
    onGroupHover(null);
  }, [onGroupHover]);

  const clearResizeGuideTimer = useCallback(() => {
    if (resizeGuideHoverTimerRef.current !== null) {
      window.clearTimeout(resizeGuideHoverTimerRef.current);
      resizeGuideHoverTimerRef.current = null;
    }
  }, []);

  const onResizeGuideMouseEnter = useCallback(() => {
    resizeGuideHoverDepthRef.current += 1;
    if (resizeGuideHoverDepthRef.current === 1) {
      setResizeGuideHoverTier(1);
      clearResizeGuideTimer();
      resizeGuideHoverTimerRef.current = window.setTimeout(() => {
        setResizeGuideHoverTier(2);
        resizeGuideHoverTimerRef.current = null;
      }, GUIDE_HOVER_DELAY_MS);
    }
  }, [clearResizeGuideTimer]);

  const onResizeGuideMouseLeave = useCallback(() => {
    resizeGuideHoverDepthRef.current -= 1;
    if (resizeGuideHoverDepthRef.current <= 0) {
      resizeGuideHoverDepthRef.current = 0;
      clearResizeGuideTimer();
      setResizeGuideHoverTier(0);
    }
  }, [clearResizeGuideTimer]);
  
  // コンポーネントのアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (confirmTimeoutRef.current !== null) {
        window.clearTimeout(confirmTimeoutRef.current);
      }
      if (groupGuideHoverTimerRef.current !== null) {
        window.clearTimeout(groupGuideHoverTimerRef.current);
      }
      if (resizeGuideHoverTimerRef.current !== null) {
        window.clearTimeout(resizeGuideHoverTimerRef.current);
      }
      if (editGuideTimerRef.current !== null) {
        window.clearTimeout(editGuideTimerRef.current);
      }
      // 削除ボタンのタイマーもクリア
      clearDeleteTimers();
    };
  }, [clearDeleteTimers]);
  
  // タイトルが変更された時にeditTitleを更新
  useEffect(() => {
    setEditTitle(group.title || '');
  }, [group.title]);

  useEffect(() => {
    if (!isTitleEditing) {
      if (editGuideTimerRef.current !== null) {
        window.clearTimeout(editGuideTimerRef.current);
        editGuideTimerRef.current = null;
      }
      setEditGuideTier(1);
      return;
    }
    setEditGuideTier(1);
    if (editGuideTimerRef.current !== null) {
      window.clearTimeout(editGuideTimerRef.current);
      editGuideTimerRef.current = null;
    }
    editGuideTimerRef.current = window.setTimeout(() => {
      setEditGuideTier(2);
      editGuideTimerRef.current = null;
    }, GUIDE_HOVER_DELAY_MS);
    return () => {
      if (editGuideTimerRef.current !== null) {
        window.clearTimeout(editGuideTimerRef.current);
        editGuideTimerRef.current = null;
      }
    };
  }, [isTitleEditing]);
  
  // パスの境界がない場合はレンダリングしない
  if (!pathBounds) return null;

  // タイトル変更時のハンドラ
  const handleTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditTitle(e.target.value);
    setIsTitleEditing(true);
  };
  
  // タイトル編集確定
  const handleTitleConfirm = () => {
    if (!isTitleEditing) return;
    
    onUpdateGroupTitle(group.id, editTitle);
    setIsTitleEditing(false);
    
    // 確定フラグを設定してタイムアウト後にリセット
    justConfirmedRef.current = true;
    
    // 前回のタイマーがあれば解除
    if (confirmTimeoutRef.current !== null) {
      window.clearTimeout(confirmTimeoutRef.current);
    }
    
    // ActiveSheetComponentの確定フラグも設定する
    onEditConfirm();
    
    // 確定フラグを一定時間後にリセット
    confirmTimeoutRef.current = window.setTimeout(() => {
      justConfirmedRef.current = false;
      confirmTimeoutRef.current = null;
    }, 300);
  };

  const handleTitleBlur = () => {
    if (titleEscapeCancelRef.current) {
      titleEscapeCancelRef.current = false;
      return;
    }
    handleTitleConfirm();
  };
  
  // キー入力処理
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // IME入力中は何もしない（確定待ち）
    if (isComposing) {
      return;
    }
    
    if (e.key === 'Enter') {
      // Shift+Enterで改行を入力
      if (e.shiftKey) {
        return; // デフォルトの改行動作を許可
      }
      e.preventDefault();
      e.stopPropagation();
      handleTitleConfirm();
      titleInputRef.current?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      titleEscapeCancelRef.current = true;
      setEditTitle(group.title || '');
      setIsTitleEditing(false);
      titleInputRef.current?.blur();
    }
  };


  // 書き出しボタンのクリックハンドラー
  const handleExportGroup = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // ドラッグ操作を防止
    onExportGroup(group.id);
  };

  // 正負転換ボタンのクリックハンドラー
  const handleToggleGroupSign = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // ドラッグ操作を防止
    onToggleGroupSign(group.id);
  };

  // カラー変更ボタンのクリックハンドラー
  const handleChangeColor = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // ドラッグ操作を防止
    
    // 修飾キーが押されているかチェック
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    let isShiftPressed = false;
    let isCtrlOptionPressed = false;
    
    if (e.type === 'click' || e.type === 'mousedown' || e.type === 'mouseup') {
      // マウスイベントの場合
      const mouseEvent = e as React.MouseEvent;
      isShiftPressed = mouseEvent.shiftKey || false;
      isCtrlOptionPressed = isMac ? mouseEvent.altKey : mouseEvent.ctrlKey;
    } else {
      // タッチイベントの場合
      isShiftPressed = (window as any).__shiftKeyPressed || false;
      isCtrlOptionPressed = isMac ? (window as any).__altKeyPressed : (window as any).__ctrlKeyPressed;
    }
    
    
    onChangeGroupColor(group.id, isShiftPressed, isCtrlOptionPressed);
  };

  // 共通の削除開始処理（マウス・タッチ共通）
  const handleDeleteStart = (e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();
    e.preventDefault();
    
    
    // 修飾キーが押されているかチェック
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    let isCtrlOptionPressed = false;
    let isShiftPressed = false;
    let isAltControlPressed = false;
    
    if (eventType === 'mouse') {
      // マウスイベントの場合
      const mouseEvent = e as React.MouseEvent;
      isCtrlOptionPressed = isMac ? mouseEvent.altKey : mouseEvent.ctrlKey;
      isShiftPressed = mouseEvent.shiftKey || false;
      isAltControlPressed = isMac ? mouseEvent.ctrlKey : mouseEvent.altKey;
    } else {
      // タッチイベントの場合、現在の修飾キー状態をチェック
      // タッチイベントでは修飾キーの状態が直接取得できないため、
      // グローバルな修飾キー状態を追跡する必要がある
      // 簡易実装として、キーボードイベントリスナーで状態を追跡
      isCtrlOptionPressed = isMac ? (window as any).__altKeyPressed : (window as any).__ctrlKeyPressed;
      isShiftPressed = (window as any).__shiftKeyPressed || false;
      isAltControlPressed = isMac ? (window as any).__ctrlKeyPressed : (window as any).__altKeyPressed;
    }
    
    
    // 修飾キー状態を保存
    deleteModifierKeysRef.current = {
      ctrlOption: isCtrlOptionPressed,
      shift: isShiftPressed,
      altControl: isAltControlPressed
    };
    
    deleteStartTimeRef.current = Date.now();
    
    // 修飾キーが押されている場合は、開始時に修飾キー相当のプレビューを表示
    if (deleteModifierKeysRef.current) {
      const { ctrlOption, shift, altControl } = deleteModifierKeysRef.current;
      
      if (ctrlOption || shift || altControl) {
        // 修飾キー相当の状態に変更
        let targetPhase = DELETE_PHASES.MAIN_ONLY;
        
        if (ctrlOption && !shift && !altControl) {
          targetPhase = DELETE_PHASES.ALL_DELETE; // Ctrl/Option: 本・内・パ削除
        } else if (shift && ctrlOption && !altControl) {
          targetPhase = DELETE_PHASES.MAIN_WITH_CONTAINED; // Shift+Ctrl/Option: 本・内削除
        } else if (altControl && !shift && !ctrlOption) {
          targetPhase = DELETE_PHASES.CONTAINED_WITH_POUCHES; // Alt/Control: 内・パ削除
        } else if (shift && altControl && !ctrlOption) {
          targetPhase = DELETE_PHASES.CONTAINED_ONLY; // Shift+Alt/Control: 内削除
        }
        
        // その状態で固定（以後変化させない）
        applyDeletePhase(targetPhase, false); // false = 保存しない
        
        deletePhaseTimersRef.current = []; // タイマーは設定しない
        return; // 通常の段階変更処理をスキップ
      }
    }
    
    // 修飾キーが押されていない場合は、通常の処理
    // 開始と同時に本グループにdelete-previewクラスを追加
    const groupElement = document.querySelector(`[data-group-id="${group.id}"]`);
    if (groupElement) {
      groupElement.classList.add('delete-preview');
    }
    
    // 長押し検出タイマーを開始
    deleteLongPressTimerRef.current = window.setTimeout(() => {
      // 長押し検出
      
      // すべてのプレビュークラスをクリア
      const allGroupElements = document.querySelectorAll('[data-group-id]');
      allGroupElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      const allPouchElements = document.querySelectorAll('[data-pouch-id]');
      allPouchElements.forEach(element => {
        element.classList.remove('delete-preview');
      });
      
      // 長押しが検出された場合の処理
      const targets = checkDeleteTargets();
      
      // 通常の段階的削除処理
      const phases: number[] = [];
      if (targets.canDeleteMain) phases.push(DELETE_PHASES.MAIN_ONLY);
      if (targets.canDeleteContained) phases.push(DELETE_PHASES.CONTAINED_ONLY);
      if (targets.canDeleteContained || targets.canDeletePouches) phases.push(DELETE_PHASES.CONTAINED_WITH_POUCHES); // 内包グループまたはパウチがある場合
      if (targets.canDeleteMain && targets.canDeleteContained) phases.push(DELETE_PHASES.MAIN_WITH_CONTAINED);
      if (targets.canDeleteMain && (targets.canDeleteContained || targets.canDeletePouches)) phases.push(DELETE_PHASES.ALL_DELETE); // 本グループと（内包グループまたはパウチ）がある場合
      
      
      // 即座に最初のフェーズを表示
      if (phases.length > 0) {
        applyDeletePhase(phases[0], false);
      }
      
      // デバッグログを削除
      
      // フェーズを順次実行（最初のフェーズは既に表示済み）
      const phaseTimers: number[] = [];
      phases.forEach((phase, index) => {
        // 最初のフェーズ（index=0）は既に表示済みなのでスキップ
        if (index === 0) return;
        
        const delay = (index - 1) * 1000; // 0秒、1秒、2秒、3秒...（最初のフェーズは既に表示済み）
        const phaseNames = {
          [DELETE_PHASES.MAIN_ONLY]: 'MAIN_ONLY (本削除)',
          [DELETE_PHASES.CONTAINED_ONLY]: 'CONTAINED_ONLY (内削除)',
          [DELETE_PHASES.CONTAINED_WITH_POUCHES]: 'CONTAINED_WITH_POUCHES (内・パ削除)',
          [DELETE_PHASES.MAIN_WITH_CONTAINED]: 'MAIN_WITH_CONTAINED (本・内削除)',
          [DELETE_PHASES.ALL_DELETE]: 'ALL_DELETE (本・内・パ削除)'
        };
        
        const timerId = window.setTimeout(() => {
          applyDeletePhase(phase, false); // false = 保存しない
        }, delay);
        
        phaseTimers.push(timerId);
      });
      
      deletePhaseTimersRef.current = phaseTimers;
    }, 1000); // 1秒で長押し検出
  };

  // 削除ボタンのマウスダウン処理
  const handleDeleteMouseDown = (e: React.MouseEvent) => {
    handleDeleteStart(e, 'mouse');
  };

  // 削除ボタンのタッチスタート処理
  const handleDeleteTouchStart = (e: React.TouchEvent) => {
    handleDeleteStart(e, 'touch');
  };
  
  // 共通の削除終了処理（マウス・タッチ共通）
  const handleDeleteEnd = (e: React.MouseEvent | React.TouchEvent, eventType: 'mouse' | 'touch') => {
    e.stopPropagation();
    
    
    // 長押しタイマーをクリア
    if (deleteLongPressTimerRef.current !== null) {
      window.clearTimeout(deleteLongPressTimerRef.current);
      deleteLongPressTimerRef.current = null;
    }
    
    // 長押しが検出されていた場合
    if (deleteStartTimeRef.current > 0) {
      const holdDuration = Date.now() - deleteStartTimeRef.current;
      
      if (holdDuration >= 1000) {
        // 長押しが検出されていた場合
        
        // 修飾キーが押されている場合は、修飾キー相当のフェーズで削除
        if (deleteModifierKeysRef.current) {
          const { ctrlOption, shift, altControl } = deleteModifierKeysRef.current;
          
          if (ctrlOption || shift || altControl) {
            let targetPhase = DELETE_PHASES.MAIN_ONLY;
            
            if (ctrlOption && !shift && !altControl) {
              targetPhase = DELETE_PHASES.ALL_DELETE; // Ctrl/Option: 本・内・パ削除
            } else if (shift && ctrlOption && !altControl) {
              targetPhase = DELETE_PHASES.MAIN_WITH_CONTAINED; // Shift+Ctrl/Option: 本・内削除
            } else if (altControl && !shift && !ctrlOption) {
              targetPhase = DELETE_PHASES.CONTAINED_WITH_POUCHES; // Alt/Control: 内・パ削除
            } else if (shift && altControl && !ctrlOption) {
              targetPhase = DELETE_PHASES.CONTAINED_ONLY; // Shift+Alt/Control: 内削除
            }
            
            applyDeletePhase(targetPhase, true); // true = 保存する
            return;
          }
        }
        
        // 修飾キーが押されていない場合は、長押し時間に基づいて正しいフェーズを決定
        const targets = checkDeleteTargets();
        const phases: number[] = [];
        if (targets.canDeleteMain) phases.push(DELETE_PHASES.MAIN_ONLY);
        if (targets.canDeleteContained) phases.push(DELETE_PHASES.CONTAINED_ONLY);
        if (targets.canDeleteContained || targets.canDeletePouches) phases.push(DELETE_PHASES.CONTAINED_WITH_POUCHES); // 内包グループまたはパウチがある場合
        if (targets.canDeleteMain && targets.canDeleteContained) phases.push(DELETE_PHASES.MAIN_WITH_CONTAINED);
        if (targets.canDeleteMain && (targets.canDeleteContained || targets.canDeletePouches)) phases.push(DELETE_PHASES.ALL_DELETE); // 本グループと（内包グループまたはパウチ）がある場合
        
        // 長押し時間に基づいて、実際に実行可能なフェーズを決定
        let nextPhase = phases[0]; // デフォルト（最初のフェーズ）
        if (holdDuration >= 4000 && phases.length > 4) {
          nextPhase = phases[4]; // 4秒以上
        } else if (holdDuration >= 3000 && phases.length > 3) {
          nextPhase = phases[3]; // 3秒以上
        } else if (holdDuration >= 2000 && phases.length > 2) {
          nextPhase = phases[2]; // 2秒以上
        } else if (holdDuration >= 1000 && phases.length > 1) {
          nextPhase = phases[1]; // 1秒以上
        }
        
        // まず正しいプレビューを表示
        applyDeletePhase(nextPhase, false); // false = プレビューのみ
        // その後実際の削除を実行
        applyDeletePhase(nextPhase, true); // true = 保存する
      } else {
        // 短いクリックの場合、通常の削除処理
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        let isCtrlOptionPressed = false;
        let isShiftPressed = false;
        let isAltControlPressed = false;
        
        if (eventType === 'mouse') {
          const mouseEvent = e as React.MouseEvent;
          isCtrlOptionPressed = isMac ? mouseEvent.altKey : mouseEvent.ctrlKey;
          isShiftPressed = mouseEvent.shiftKey || false;
          isAltControlPressed = isMac ? mouseEvent.ctrlKey : mouseEvent.altKey;
        } else {
          isCtrlOptionPressed = isMac ? (window as any).__altKeyPressed : (window as any).__ctrlKeyPressed;
          isShiftPressed = (window as any).__shiftKeyPressed || false;
          isAltControlPressed = isMac ? (window as any).__ctrlKeyPressed : (window as any).__altKeyPressed;
        }
        
        onDeleteGroup(group.id, isCtrlOptionPressed, isShiftPressed, isAltControlPressed);
      }
    } else {
      // 通常のクリック処理（短いクリック）
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      let isCtrlOptionPressed = false;
      let isShiftPressed = false;
      let isAltControlPressed = false;
      
      if (eventType === 'mouse') {
        const mouseEvent = e as React.MouseEvent;
        isCtrlOptionPressed = isMac ? mouseEvent.altKey : mouseEvent.ctrlKey;
        isShiftPressed = mouseEvent.shiftKey || false;
        isAltControlPressed = isMac ? mouseEvent.ctrlKey : mouseEvent.altKey;
      } else {
        isCtrlOptionPressed = isMac ? (window as any).__altKeyPressed : (window as any).__ctrlKeyPressed;
        isShiftPressed = (window as any).__shiftKeyPressed || false;
        isAltControlPressed = isMac ? (window as any).__ctrlKeyPressed : (window as any).__altKeyPressed;
      }
      
      onDeleteGroup(group.id, isCtrlOptionPressed, isShiftPressed, isAltControlPressed);
    }
    
    // タイマーをクリア
    clearDeleteTimers();
  };

  // 削除ボタンのマウスアップ処理
  const handleDeleteMouseUp = (e: React.MouseEvent) => {
    handleDeleteEnd(e, 'mouse');
  };

  // 削除ボタンのタッチエンド処理
  const handleDeleteTouchEnd = (e: React.TouchEvent) => {
    handleDeleteEnd(e, 'touch');
  };

  // 削除ボタンのマウスリーブ処理
  const handleDeleteMouseLeave = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // 長押しタイマーをクリア
    if (deleteLongPressTimerRef.current !== null) {
      window.clearTimeout(deleteLongPressTimerRef.current);
      deleteLongPressTimerRef.current = null;
    }
    
    // マウスリーブ時は削除をキャンセル（何も起きない）
    
    // タイマーをクリア
    clearDeleteTimers();
  };

  // 削除ボタンのタッチキャンセル処理
  const handleDeleteTouchCancel = (e: React.TouchEvent) => {
    e.stopPropagation();
    
    // 長押しタイマーをクリア
    if (deleteLongPressTimerRef.current !== null) {
      window.clearTimeout(deleteLongPressTimerRef.current);
      deleteLongPressTimerRef.current = null;
    }
    
    // タッチキャンセル時は削除をキャンセル（何も起きない）
    
    // タイマーをクリア
    clearDeleteTimers();
  };
  
  // 削除ボタンのクリックハンドラー（従来の処理）
  const handleDeleteGroup = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // ドラッグ操作を防止
    
    // 修飾キーが押されているかチェック
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    // getCtrlOptionModifierKey: Mac: Option, Windows: Ctrl
    const isCtrlOptionPressed = isMac ? (e as React.MouseEvent).altKey : (e as React.MouseEvent).ctrlKey;
    const isShiftPressed = (e as React.MouseEvent).shiftKey || false;
    // getAltControlModifierKey: Mac: Ctrl, Windows: Alt
    const isAltControlPressed = isMac ? (e as React.MouseEvent).ctrlKey : (e as React.MouseEvent).altKey;
    
    onDeleteGroup(group.id, isCtrlOptionPressed, isShiftPressed, isAltControlPressed);
  };

  // サブトータルクリックハンドラー（グループトータルパウチ作成）
  const handleSubtotalClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation(); // ドラッグ操作を防止
    
    // クリック位置を取得
    let clientX: number, clientY: number;
    if ('touches' in e) {
      // タッチイベントの場合
      if (e.touches.length > 0) {
        // onTouchStartの場合
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else if ('changedTouches' in e && e.changedTouches.length > 0) {
        // onTouchEndの場合
        clientX = e.changedTouches[0].clientX;
        clientY = e.changedTouches[0].clientY;
      } else {
        return; // タッチ位置が取得できない場合は処理を中断
      }
    } else {
      // マウスイベントの場合
      const mouseEvent = e as React.MouseEvent;
      clientX = mouseEvent.clientX;
      clientY = mouseEvent.clientY;
    }
    
    onCreateGroupTotalPouch(group.id, clientX, clientY);
  };
  
  return (
    <div 
      className={`group-wrapper ${group._isCloning ? 'cloning' : ''} ${(!group.pouches || group.pouches.length === 0) ? 'no-pouches' : ''} ${isTableSelected ? 'table-selected' : ''} ${group.color === 'mute' ? 'mute-group' : ''} ${group._isInMuteGroup ? 'in-mute-group' : ''} ${group._isAdding ? 'group-adding' : ''} ${group._isDeleting ? 'group-deleting' : ''} ${group._isDragging ? 'dragging-flag' : ''} ${groupClassName}`}
      data-group-id={group.id}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        '--group-color': group.color === 'mute' ? '#666666' : (GROUP_COLORS.find(color => color.id === group.color)?.value || GROUP_COLORS[0].value)
      } as React.CSSProperties}
    >
      {/* SVG要素 */}
      <svg
        className="group-svg"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          overflow: 'visible'
        }}
      >
        <path
          d={group.path}
          className="group-path"
          data-group-id={group.id}
        />
      </svg>
      
      {/* パスと同じ位置・サイズのコンテナ */}
      <div 
        className={`group-container ${isTotalGroup ? 'total-group' : ''}`}
        data-deleting={group._isDeleting ? 'true' : 'false'}
        {...(groupContainerTooltipHtml
          ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupContainerTooltipHtml }
          : {})}
        style={{
          position: 'absolute',
          left: pathBounds.left,
          top: pathBounds.top,
          width: pathBounds.right - pathBounds.left,
          height: pathBounds.bottom - pathBounds.top,
          pointerEvents: 'all'
        }}
        onMouseDown={(e) => {
          onMouseDown(e, group.id, 'group');
        }}
        onTouchStart={(e) => {
          onTouchStart(e, group.id, 'group');
        }}
        onMouseUp={(e) => onMouseUp(e as unknown as MouseEvent)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* グループタイトル入力欄 - 左上 */}
        <div className="group-title-container">
          <textarea
            ref={titleInputRef}
            className={`group-title-input${editTitle.length >= 30 ? ' long-title' : ''}`}
            {...(isTitleEditing
              ? groupEditModeTooltipHtml
                ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupEditModeTooltipHtml }
                : {}
              : groupTitleTooltipHtml
                ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupTitleTooltipHtml }
                : {})}
            placeholder={(() => {
              if (isTotalGroup && sheetTitle) {
                // TOTAL表示の条件を満たす場合は、シート名をプレースホルダーとして使用
                return sheetTitle;
              }
              return group.placeholderText || "No Title";
            })()}
            value={editTitle}
            onChange={handleTitleChange}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            onKeyDown={handleKeyDown}
            onBlur={handleTitleBlur}
            rows={1}
            onMouseDown={(e) => {
              wasTitleFocusedOnMouseDown.current = document.activeElement === e.currentTarget;
              if (wasTitleFocusedOnMouseDown.current) {
                e.stopPropagation();
              }
            }}
            onMouseMove={(e) => {
              if (wasTitleFocusedOnMouseDown.current) {
                e.stopPropagation();
              }
            }}
            onMouseUp={(e) => {
              if (wasTitleFocusedOnMouseDown.current) {
                e.stopPropagation();
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!group._isDragging) {
                const target = e.currentTarget;
                target.focus();
                target.select();
              }
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              const target = e.currentTarget;
              const wasFocused = document.activeElement === target;
              if (!wasFocused) {
                requestAnimationFrame(() => {
                  target.focus();
                  target.select();
                });
              }
            }}
          />
        </div>
        
        {/* グループ操作ボタン - 右上に縦に並べる */}
        <div className="group-buttons">
          {/* 書き出しボタン - 1番目（一番上） */}
          <button 
            className="group-export-button"
            data-tooltip-id="tt" data-tooltip-html={groupExportTooltipHtml}
            onClick={handleExportGroup}
            onTouchEnd={(e) => {
              const isDragging = onCheckGroupDragging ? onCheckGroupDragging(group.id) : group._isDragging;
              if (!isDragging) {
                handleExportGroup(e);
              }
            }}
            disabled={!group.pouches || group.pouches.length === 0}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              stroke="var(--icon-color)"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 6l-4-4-4 4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 2v13" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* 正負転換ボタン - 2番目 */}
          <button 
            className="group-toggle-sign-button"
            data-tooltip-id="tt" data-tooltip-html={groupToggleSignTooltipHtml}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.type === 'click' && e.clientX === 0 && e.clientY === 0) {
                // タッチイベントから発火された人工的なクリックは無視
                return;
              }
              handleToggleGroupSign(e);
            }}
            onTouchEnd={(e) => {
              const isDragging = onCheckGroupDragging ? onCheckGroupDragging(group.id) : group._isDragging;
              if (!isDragging) {
                e.preventDefault();
                e.stopPropagation();
                handleToggleGroupSign(e);
              }
            }}
            disabled={!group.pouches || group.pouches.length === 0}
          >
            <svg 
              viewBox="0 0 150 150" 
              fill="var(--icon-color)"
              xmlns="http://www.w3.org/2000/svg"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M77.955,109.975c-4.037,0-7.309,3.271-7.309,7.308c0,4.037,3.272,7.31,7.309,7.31h56.029c4.037,0,7.309-3.271,7.309-7.31
                c0-4.036-3.271-7.308-7.309-7.308H77.955z"/>
              <path d="M0,44.2c0,4.037,3.272,7.308,7.309,7.308h19.489v19.49c0,4.036,3.271,7.309,7.308,7.309c4.037,0,7.309-3.271,7.309-7.309
                v-19.49H63.34c4.037,0,7.308-3.271,7.308-7.308c0-4.037-3.271-7.309-7.308-7.309H41.414V12.53c0-4.037-3.272-7.308-7.309-7.308
                c-4.037,0-7.308,3.271-7.308,7.308v24.361H7.308C3.272,36.892,0,40.164,0,44.2z"/>
              <path d="M131.428,17.261c-2.854-2.855-7.481-2.855-10.336,0L14.763,123.594c-2.853,2.854-2.853,7.481,0,10.334
                c1.425,1.429,3.298,2.143,5.167,2.143c1.868,0,3.742-0.714,5.169-2.143L131.428,27.599
                C134.283,24.746,134.283,20.114,131.428,17.261z"/>
            </svg>
          </button>

          {/* カラー変更ボタン - 3番目 */}
          <button 
            className="group-color-button"
            data-tooltip-id="tt" data-tooltip-html={groupColorButtonTooltipHtml}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleColorStart(e, 'mouse');
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleColorEnd(e, 'mouse');
            }}
            onMouseLeave={handleColorCancel}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleColorStart(e, 'touch');
            }}
            onTouchEnd={(e) => {  
              e.preventDefault();
              e.stopPropagation();
              handleColorEnd(e, 'touch');
            }}
            onTouchCancel={handleColorCancel}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.type === 'click' && e.clientX === 0 && e.clientY === 0) {
                // タッチイベントから発火された人工的なクリックは無視
                return;
              }
            }}
          >
            <svg 
              viewBox="0 0 1024 1024" 
              version="1.1" 
              xmlns="http://www.w3.org/2000/svg"
              fill="var(--icon-color)"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M833.706667 216.746667A452.693333 452.693333 0 0 0 508.16 85.333333a426.666667 426.666667 0 0 0-2.133333 853.333334 110.08 110.08 0 0 0 107.946666-80.64 107.52 107.52 0 0 0-24.32-97.28 21.333333 21.333333 0 0 1 15.786667-35.413334h70.4A262.4 262.4 0 0 0 938.666667 483.413333a361.813333 361.813333 0 0 0-104.96-266.666666z m-541.866667 412.16a64 64 0 1 1 17.066667-88.746667 63.573333 63.573333 0 0 1-17.066667 88.746667zM354.133333 394.666667a64 64 0 1 1-23.466666-85.333334 64 64 0 0 1 23.466666 85.333334zM469.333333 298.666667a64 64 0 1 1 64-64A64 64 0 0 1 469.333333 298.666667z m245.333334 34.133333a64 64 0 1 1 23.466666-85.333333 64 64 0 0 1-23.466666 85.333333z" />
            </svg>
          </button>

          {/* マージボタン - 4番目 */}
          <button 
            className={`group-merge-button ${isMergeDisabled ? 'disabled' : ''}`}
            data-tooltip-id="tt" data-tooltip-html={groupMergeTooltipHtml}
            onMouseDown={(e) => handleMergeStart(e, 'mouse')}
            onMouseUp={(e) => handleMergeEnd(e, 'mouse')}
            onMouseLeave={handleMergeCancel}
            onTouchStart={(e) => handleMergeStart(e, 'touch')}
            onTouchEnd={(e) => handleMergeEnd(e, 'touch')}
            onTouchCancel={handleMergeCancel}
            onContextMenu={(e) => {
              e.preventDefault(); // 右クリックメニューを無効化
            }}
            disabled={isMergeDisabled}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              stroke="var(--icon-color)"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M21 12L15 12L10 12C10 12 6 12 3 7M10 12C10 12 6 12 3 17" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 8L21 12L16 16" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* 削除ボタン - 5番目（一番下） */}
          <button 
            className="group-delete-button"
            data-tooltip-id="tt" data-tooltip-html={groupDeleteButtonTooltipHtml}
            onMouseDown={handleDeleteMouseDown}
            onMouseUp={handleDeleteMouseUp}
            onMouseLeave={handleDeleteMouseLeave}
            onTouchStart={handleDeleteTouchStart}
            onTouchEnd={handleDeleteTouchEnd}
            onTouchCancel={handleDeleteTouchCancel}
            onContextMenu={(e) => {
              e.preventDefault(); // 右クリックメニューを無効化
            }}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              stroke="var(--icon-color)"
              strokeWidth="2"
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ 
                width: 'var(--icon-size)', 
                height: 'var(--icon-size)' 
              }}
            >
              <path d="M3 6h18"></path>
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
        </div>

        {/* Subtotal */}
        <div 
          className="group-subtotal clickable-subtotal"
          onMouseUp={(e) => {
            // ドラッグ中でない場合のみGTP作成
              e.stopPropagation(); // 親のhandleMouseUpが実行されないようにする
              e.preventDefault(); // デフォルトの動作を防ぐ
              // デバッグ情報を追加
              onResetInteraction(); // ドラッグ状態をリセット
              // 新規パウチ作成と同じロジックを使用
              onCreateGroupTotalPouch(group.id, e.clientX, e.clientY);
          }}
            onTouchEnd={(e) => {
                const isDragging = onCheckGroupDragging ? onCheckGroupDragging(group.id) : group._isDragging;
                if (!isDragging) {
                  const touch = e.changedTouches[0];
                  if (touch) {
                      onCreateGroupTotalPouch(group.id, touch.clientX, touch.clientY);
                  }
                } else {
                }
            }}
          data-tooltip-id="tt" data-tooltip-html={groupAddGtpTooltipHtml}
        >
          {isTotalGroup ? 'TOTAL' : 'SUBTOTAL'}{' '}
            {shouldShowTimeConversion() && (
              <strong style={groupSubtotalContribution < 0 ? { color: 'var(--negative-color)' } : undefined}>
                {(() => {
                  const totalValue = groupSubtotalContribution;
                  const totalMinutes = Math.round(Math.abs(totalValue));
                  const hours = Math.floor(totalMinutes / 60);
                  const minutes = totalMinutes % 60;
                  const sign = totalValue < 0 ? '-' : '';
                  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
                })()}
              </strong>
            )}
            <NumberCounter
              key={`group-counter-${group.id}`}
              value={Math.abs(groupSubtotalContribution)}
              isNegative={groupSubtotalContribution < 0}
              animateFromZero={isInitialLoading || isSheetSwitching || group._previousSubtotal === 0}
              groupId={group.id}
              duration={(isInitialLoading || isSheetSwitching) ? 300 : 300}
              showTimeConversion={shouldShowTimeConversion()}
            />
        </div>
        
        {/* リサイズハンドル - 右下（タッチ・非タッチ共通） */}
        <div
          className="resize-handle resize-handle-br"
          data-group-id={group.id}
          data-resize-corner="br"
          {...(groupResizeTooltipHtml
            ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupResizeTooltipHtml }
            : {})}
          onMouseEnter={onResizeGuideMouseEnter}
          onMouseLeave={onResizeGuideMouseLeave}
          onMouseDown={(e) => {
            e.stopPropagation(); // 親要素へのイベント伝播を防止
            onMouseDown(e, group.id, 'resize-handle');
          }}
          onTouchStart={(e) => {
            e.stopPropagation(); // 親要素へのイベント伝播を防止
            onTouchStart(e, group.id, 'resize-handle');
          }}
        />
        {/* リサイズハンドル - 左下（非タッチデバイスのみ） */}
        <div
          className="resize-handle resize-handle-bl"
          data-group-id={group.id}
          data-resize-corner="bl"
          {...(groupResizeTooltipHtml
            ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupResizeTooltipHtml }
            : {})}
          onMouseEnter={onResizeGuideMouseEnter}
          onMouseLeave={onResizeGuideMouseLeave}
          onMouseDown={(e) => {
            e.stopPropagation();
            onMouseDown(e, group.id, 'resize-handle');
          }}
        />
        {/* リサイズハンドル - 右上（非タッチデバイスのみ） */}
        <div
          className="resize-handle resize-handle-tr"
          data-group-id={group.id}
          data-resize-corner="tr"
          {...(groupResizeTooltipHtml
            ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupResizeTooltipHtml }
            : {})}
          onMouseEnter={onResizeGuideMouseEnter}
          onMouseLeave={onResizeGuideMouseLeave}
          onMouseDown={(e) => {
            e.stopPropagation();
            onMouseDown(e, group.id, 'resize-handle');
          }}
        />
        {/* リサイズハンドル - 左上（非タッチデバイスのみ） */}
        <div
          className="resize-handle resize-handle-tl"
          data-group-id={group.id}
          data-resize-corner="tl"
          {...(groupResizeTooltipHtml
            ? { 'data-tooltip-id': 'tt', 'data-tooltip-html': groupResizeTooltipHtml }
            : {})}
          onMouseEnter={onResizeGuideMouseEnter}
          onMouseLeave={onResizeGuideMouseLeave}
          onMouseDown={(e) => {
            e.stopPropagation();
            onMouseDown(e, group.id, 'resize-handle');
          }}
        />
      </div>
    </div>
  );
};

// 描画中のグループを表示するコンポーネント
export const PendingGroupComponent: React.FC<PendingGroupProps> = ({
  pendingPath,
  lastGroupColor,
  isTooSmall = false
}) => {
  if (!pendingPath) return null;
    
  return (
    <div
      className={`group-wrapper pending-group-wrapper ${isTooSmall ? 'too-small' : ''}`}
      style={{
        '--group-color': lastGroupColor === 'mute' ? '#666666' : (GROUP_COLORS.find(color => color.id === lastGroupColor)?.value || GROUP_COLORS[0].value)
      } as React.CSSProperties}
    >
      <svg
        className="group-svg"
      >
        <path
          d={pendingPath}
          className={`group-path ${isTooSmall ? 'to-small' : ''}`}
        />
      </svg>
    </div>
  );
};

// React.memoで再レンダリングを最適化
export default React.memo(GroupComponent, (prevProps, nextProps) => {
  // グループの基本プロパティが変更された場合のみ再レンダリング
  // パウチの値や乗数が変更された場合も再レンダリングが必要

  const groupPropsEqual = (
    prevProps.group.id === nextProps.group.id &&
    prevProps.group.path === nextProps.group.path &&
    prevProps.group.color === nextProps.group.color &&
    prevProps.group.originalColor === nextProps.group.originalColor &&
    prevProps.group.title === nextProps.group.title &&
    prevProps.group.pouches?.length === nextProps.group.pouches?.length &&
    prevProps.group.pouches?.every((id, index) => id === nextProps.group.pouches?.[index]) &&
    // 削除状態もチェック（削除アニメーション中は再レンダリングが必要）
    prevProps.group._isDeleting === nextProps.group._isDeleting &&
    // ドラッグ状態もチェック（ドラッグ中の視覚変化のため再レンダリングが必要）
    (prevProps.group as any)._isDragging === (nextProps.group as any)._isDragging &&
    // 複製状態もチェック（複製中の視覚変化のため再レンダリングが必要）
    (prevProps.group as any)._isCloning === (nextProps.group as any)._isCloning
  );
  
  // パウチの値や乗数が変更されたかチェック（GTP関連も含む）
  const pouchesEqual = prevProps.pouches.length === nextProps.pouches.length &&
    prevProps.pouches.every((prevPouch, index) => {
      const nextPouch = nextProps.pouches[index];
      return nextPouch && 
        prevPouch.id === nextPouch.id &&
        prevPouch.value === nextPouch.value &&
        prevPouch.multiplier === nextPouch.multiplier &&
        prevPouch.isNegative === nextPouch.isNegative &&
        // GTP関連のプロパティもチェック
        prevPouch.isGroupTotalPouch === nextPouch.isGroupTotalPouch &&
        prevPouch.referencedGroupId === nextPouch.referencedGroupId &&
        prevPouch.oppositeToTotal === nextPouch.oppositeToTotal;
    });
  
  // allGroupsの変更をチェック（ミュートグループの変更を検出するため）
  const allGroupsEqual = (
    prevProps.allGroups?.length === nextProps.allGroups?.length &&
    (prevProps.allGroups?.every((prevGroup, index) => {
      const nextGroup = nextProps.allGroups?.[index];
      return nextGroup &&
        prevGroup.id === nextGroup.id &&
        prevGroup.color === nextGroup.color &&
        prevGroup.pouches?.length === nextGroup.pouches?.length &&
        prevGroup.pouches?.every((id, pIndex) => id === nextGroup.pouches?.[pIndex]);
    }) ?? true)
  ) ?? true;
  
  return groupPropsEqual && pouchesEqual && allGroupsEqual &&
    prevProps.lastGroupColor === nextProps.lastGroupColor &&
    prevProps.isInitialLoading === nextProps.isInitialLoading &&
    prevProps.isSheetSwitching === nextProps.isSheetSwitching &&
    prevProps.hoveredGroupId === nextProps.hoveredGroupId &&
    prevProps.isTouchDevice === nextProps.isTouchDevice;
}); 